import { File as FileIcon, Image, Library, Paperclip } from "lucide-react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FILE_ACCEPT } from "../../features/messages/utils/attachmentRules";

interface AttachMenuProps {
  onFiles: (files: File[]) => void;
  /** Staff only: opens the library picker. */
  onLibrary?: () => void;
  disabled: boolean;
}

const itemClass =
  "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20";

/** The composer's quiet Attach icon: Photo, File and, for staff, From the library. */
const AttachMenu = ({ onFiles, onLibrary, disabled }: AttachMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === "Escape" : !root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const choose = (input: HTMLInputElement | null) => {
    setOpen(false);
    input?.click();
  };
  const picked = (event: ChangeEvent<HTMLInputElement>) => {
    onFiles(Array.from(event.target.files ?? []));
    event.target.value = "";
  };

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        className="rounded-lg p-1.5 text-grey-40 hover:bg-grey-95 disabled:opacity-50 dark:text-grey-80 dark:hover:bg-primary-20"
        aria-label={t("messages.attach.add")}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <Paperclip size={18} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute bottom-full end-0 z-20 mb-2 w-56 rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10">
          <button type="button" className={itemClass} onClick={() => choose(photoInput.current)}>
            <Image size={14} className="shrink-0" aria-hidden="true" />
            <span>{t("messages.attach.photo")}</span>
          </button>
          <button type="button" className={itemClass} onClick={() => choose(fileInput.current)}>
            <FileIcon size={14} className="shrink-0" aria-hidden="true" />
            <span>{t("messages.attach.file")}</span>
          </button>
          {onLibrary && (
            <button type="button" className={itemClass} onClick={() => { setOpen(false); onLibrary(); }}>
              <Library size={14} className="shrink-0" aria-hidden="true" />
              <span>{t("messages.attach.material")}</span>
            </button>
          )}
        </div>
      )}
      <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={picked} data-testid="attach-photo-input" />
      <input ref={fileInput} type="file" accept={FILE_ACCEPT} multiple hidden onChange={picked} data-testid="attach-file-input" />
    </div>
  );
};

export default AttachMenu;
